// @ts-nocheck
import { CR_ATTRIBUTE_ORDER, INVERTER_BRANDS } from '../constants/app.constants';
import { InverterType, NewExistingType, DomesticStatus } from '../types/api.types';
import { SystemKey, SYSTEM_KEYS } from '../constants/aurora.constants';

interface SystemEquipmentData {
  module?: {
    selected_brand?: string;
    selected_module?: {
      fullName?: string;
      wattage?: number;
      modelNumber?: string;
    };
    quantity?: number;
  };
  inverters?: {
    selected_type?: InverterType;
    selected_brand?: {
      name: string;
      inverter_type: InverterType;
    };
    selected_inverters?: {
      model: string;
      wattage?: number;
      current?: number;
      quantity: number;
    }[];
    selected_optimizer?: {
      type: string;
      current?: number;
    };
    optimizer_quantity?: number;
    rsd?: {
      type: string;
      quantity: number;
      current: string;
    };
    combiner?: string;
  };
  batteries?: {
    selected_brand?: string;
    selected_battery?: {
      battery_name: string;
      battery_model: string;
      kwh?: number;
      current?: number;
      wattage?: number;
    };
    quantity?: number;
    is_domestic?: boolean;
  };
  strings?: Record<string, string>;
}

interface FormData {
  task?: {
    selected_task?: { gid: string; name: string };
    customer_info?: {
      design_date: string;
      customer_name: string;
      street_number: string;
      street_name: string;
      city: string;
      state_short_name: string;
      zip_code: string;
      utility_name: string;
      utility_meter_number: string;
      ahj_name: string;
      texas_esi_id: string;
      parcel_number_apn: string;
    };
  };
  equipment?: {
    active_system?: SystemKey;
    system_one?: SystemEquipmentData;
    system_two?: SystemEquipmentData;
    system_three?: SystemEquipmentData;
    production?: {
      production_value?: string;
    };
  };
  electrical?: {
    msp?: {
      buss_rating?: number;
      main_breaker_rating?: number;
      manufacturer?: string;
      location?: string;
      style?: string;
      type?: string;
      cad_location?: string;
      msp_new_existing?: NewExistingType | null;
      main_breaker_new_existing?: NewExistingType | null;
    };
    mbe?: {
      buss_rating?: number;
      main_breaker_rating?: number;
      mbe_new_existing?: NewExistingType | null;
      main_breaker_new_existing?: NewExistingType | null;
    };
    blp?: {
      buss_rating?: number;
      main_breaker_rating?: number;
      blp_new_existing?: NewExistingType | null;
      main_breaker_new_existing?: NewExistingType | null;
    };
    ats?: {
      buss_rating?: number;
      main_breaker_rating?: number;
      ats_new_existing?: NewExistingType | null;
      main_breaker_new_existing?: NewExistingType | null;
      panel_name?: string;
      generator_name?: string;
    };
    production_meter?: {
      new_existing?: NewExistingType | null;
      meter_name_model?: string;
    };
    utility?: {
      service_feed_source?: string;
      distance_from_um_callout?: string;
      utility_meter_base_new_existing?: NewExistingType | null;
      utility_meter_base_name_model?: string;
    };
    subpanel?: {
      subpanel_new_existing?: NewExistingType | null;
      main_breaker_new_existing?: NewExistingType | null;
      subpanel_size?: string;
      main_breaker_rating?: number;
      feeding_method?: string;
      ocpd_size?: string;
      pv_breaker_size?: string;
      pv_breaker_location?: string;
    };
    poi?: {
      selected_poi?: string;
      location?: string;
      breaker_size?: number;
    };
    disco_1?: any;
    disco_2?: any;
    disco_3?: any;
    disco_4?: any;
    disco_5?: any;
  };
  structural?: {
    general_configuration?: {
      mount_system?: string;
      roof_mount_conduit_type?: string;
      bos_conduit?: string;
      trench?: {
        conduit_size?: string;
        length?: string;
        depth?: string;
      };
    };
    structures?: {
      structure_1?: any;
      structure_2?: any;
      structure_3?: any;
    };
  };
  documentation?: {
    bos_location_value?: string;
    ahj_value?: string;
    um_number_value?: string;
    install_notes_value?: string;
    miscellaneous_notes_value?: string;
    aurora_value?: string;
    gdrive_link_value?: string;
    notion_ahj_value?: string;
    notion_ux_value?: string;
    notion_cx_value?: string;
    nec_value?: string;
    ibc_value?: string;
    irc_value?: string;
    ifc_value?: string;
  };
}

/**
 * Removes a duplicated leading brand name from an equipment name.
 * DB battery names already include the manufacturer (e.g. "Enphase IQ 5P"),
 * so prepending the brand again would duplicate it in the CR output.
 */
const stripLeadingBrandName = (equipmentName: string, brandName: string): string => {
  const normalizedName = equipmentName.trim();
  const normalizedBrand = brandName.trim().toUpperCase();
  if (normalizedBrand && normalizedName.toUpperCase().startsWith(`${normalizedBrand} `)) {
    return normalizedName.slice(normalizedBrand.length).trim();
  }
  return normalizedName;
};

/**
 * Prefixes an equipment name with its manufacturer unless it already starts with it.
 * Required for CAD imports (e.g. "IQ COMBINER 6C" -> "ENPHASE IQ COMBINER 6C").
 */
const prependBrandName = (equipmentName: string, brandName?: string): string => {
  const normalizedName = equipmentName.trim().toUpperCase();
  const normalizedBrand = brandName?.trim().toUpperCase();
  if (!normalizedBrand || normalizedName.startsWith(normalizedBrand)) {
    return normalizedName;
  }
  return `${normalizedBrand} ${normalizedName}`;
};

const hasSystemData = (system?: SystemEquipmentData): boolean => {
  if (!system) return false;
  return !!(
    system.module?.selected_brand ||
    system.module?.selected_module ||
    (system.inverters?.selected_inverters?.length ?? 0) > 0 ||
    system.batteries?.selected_battery ||
    (system.strings && Object.keys(system.strings).length > 0)
  );
};

const getActiveSystemsWithData = (equipment?: FormData['equipment']): { key: SystemKey; data: SystemEquipmentData; index: number }[] => {
  if (!equipment) return [];
  return SYSTEM_KEYS
    .map((key, index) => ({ key, data: equipment[key], index: index + 1 }))
    .filter((entry): entry is { key: SystemKey; data: SystemEquipmentData; index: number } =>
      hasSystemData(entry.data)
    );
};

const generateSystemEquipmentOutput = (
  system: SystemEquipmentData,
  suffix: string,
  output: Record<string, any>
): void => {
  // MODULES
  if (system.module?.selected_brand && system.module.selected_module && system.module.quantity && system.module.quantity > 0) {
    const brandName = system.module.selected_brand.toUpperCase();
    const modelNumber = system.module.selected_module.modelNumber || 'UNKNOWN MODEL';
    const wattage = system.module.selected_module.wattage || 0;
    const quantity = system.module.quantity;
    output[`MODULES${suffix}`] = `${quantity} X ${brandName}: ${modelNumber} (${wattage}W) MODULES`;
  }

  // INVERTERS / MICROINVERTERS
  const hasInverters = system.inverters?.selected_inverters && system.inverters.selected_inverters.length > 0;
  if (hasInverters) {
    const selectedInverters = system.inverters!.selected_inverters!;
    const outputLabel = system.inverters!.selected_type === InverterType.MICROINVERTER
      ? `MICROINVERTERS${suffix}`
      : `INVERTERS${suffix}`;

    if (selectedInverters.length === 1) {
      const inverter = selectedInverters[0];
      const brandName = system.inverters.selected_brand?.name?.toUpperCase() || 'UNKNOWN BRAND';
      const model = inverter.model || 'UNKNOWN MODEL';
      const quantity = inverter.quantity || 0;
      output[outputLabel] = `${quantity} X ${brandName}: ${model}`;
    } else {
      const inverterLines: string[] = [];
      selectedInverters.forEach(inverter => {
        const brandName = system.inverters.selected_brand?.name?.toUpperCase() || 'UNKNOWN BRAND';
        const model = inverter.model || 'UNKNOWN MODEL';
        const quantity = inverter.quantity || 0;
        inverterLines.push(`${quantity} X ${brandName}: ${model}`);
      });
      output[outputLabel] = inverterLines.join('\n');
    }
  }

  // OPTIMIZERS
  const hasOptimizers = system.inverters?.selected_optimizer &&
    system.inverters.optimizer_quantity &&
    system.inverters.optimizer_quantity > 0 &&
    system.inverters.selected_brand?.name === INVERTER_BRANDS.SOLAREDGE;
  if (hasOptimizers) {
    const quantity = system.inverters.optimizer_quantity!;
    const type = system.inverters.selected_optimizer!.type;
    output[`OPTIMIZERS${suffix}`] = `${quantity} X SOLAREDGE: ${type}`;
  }

  // RSD
  const hasRsd = system.inverters?.rsd &&
    system.inverters.rsd.type &&
    system.inverters.rsd.quantity > 0 &&
    system.inverters.rsd.current &&
    system.inverters.selected_brand?.name?.toUpperCase() === INVERTER_BRANDS.TESLA;
  if (hasRsd) {
    const rsd = system.inverters.rsd!;
    const quantity = rsd.quantity;
    const type = rsd.type.trim();
    const current = rsd.current.trim();
    output[`RSD${suffix}`] = `${quantity} x ${type} (${current} A)`;
  }

  // BATTERY
  const hasBatteries = system.batteries?.selected_battery &&
    system.batteries.selected_brand &&
    system.batteries.quantity &&
    system.batteries.quantity > 0;
  if (hasBatteries) {
    const quantity = system.batteries.quantity!;
    const brandName = system.batteries.selected_brand!.toUpperCase();
    const batteryName = stripLeadingBrandName(
      system.batteries.selected_battery!.battery_name.toUpperCase(),
      brandName
    );
    const batteryModel = system.batteries.selected_battery!.battery_model;
    const domesticPart = system.batteries.is_domestic ? ` (${batteryModel}-DOM)` : '';
    output[`BATTERY${suffix}`] = `${quantity} X ${brandName}: ${batteryName} BATTERY (${batteryModel})${domesticPart}`;
  }

  // COMBINER
  const hasCombiner = system.inverters?.combiner && system.inverters.combiner.trim().length > 0;
  if (hasCombiner) {
    output[`COMBINER${suffix}`] = prependBrandName(
      system.inverters.combiner!,
      system.inverters!.selected_brand?.name
    );
  }

  // STRINGS
  const hasStrings = system.strings && Object.keys(system.strings).length > 0;
  if (hasStrings) {
    const stringEntries = Object.entries(system.strings!)
      .sort(([a], [b]) => {
        const numA = parseInt(a.replace('string_', ''));
        const numB = parseInt(b.replace('string_', ''));
        return numA - numB;
      })
      .filter(([, value]) => typeof value === 'string' && value && value.trim());

    if (stringEntries.length > 0) {
      const formattedStrings = stringEntries.map(([, value]) => value);
      output[`STRINGS${suffix}`] = formattedStrings.join(', ');
    }
  }
};

export const generateCROutput = (formData: FormData): Record<string, any> => {
  const output: Record<string, any> = {};

  // Determine which systems have data and compute suffix strategy
  const activeSystems = getActiveSystemsWithData(formData.equipment);
  const useSystemSuffix = activeSystems.length > 1;

  // Process attributes in the exact order specified in CR_ATTRIBUTE_ORDER
  CR_ATTRIBUTE_ORDER.forEach(attribute => {
    switch (attribute) {
      case 'CUSTOMER':
        if (formData.task?.customer_info) {
          const info = formData.task.customer_info;
          let customerString = info.customer_name ? info.customer_name.toUpperCase() : '';
          
          // Build address part
          const addressParts = [];
          if (info.street_number || info.street_name) {
            const street = [info.street_number, info.street_name].filter(Boolean).join(' ');
            if (street) addressParts.push(street.toUpperCase());
          }
          if (info.city && info.zip_code) {
            addressParts.push(`${info.city.toUpperCase()} ${info.zip_code}`);
          } else if (info.city) {
            addressParts.push(info.city.toUpperCase());
          } else if (info.zip_code) {
            addressParts.push(info.zip_code);
          }
          if (info.state_short_name) {
            addressParts.push(info.state_short_name.toUpperCase());
          }
          
          // Add ESI ID for Texas
          if (info.texas_esi_id && info.state_short_name && 
              (info.state_short_name.toLowerCase() === 'tx' || info.state_short_name.toLowerCase() === 'texas')) {
            addressParts.push(`ESI:${info.texas_esi_id}`);
          }
          
          if (addressParts.length > 0) {
            customerString += ` (${addressParts.join(', ')})`;
          }
          
          if (customerString) {
            output['CUSTOMER'] = customerString;
          }
        }
        break;
        
      case 'UTILITY':
        if (formData.task?.customer_info?.utility_name) {
          output['UTILITY'] = formData.task.customer_info.utility_name.toUpperCase();
        }
        break;
        
      case 'APN':
        if (formData.task?.customer_info?.parcel_number_apn) {
          output['APN'] = formData.task.customer_info.parcel_number_apn.toUpperCase();
        }
        break;
        
      case 'AHJ':
        if (formData.task?.customer_info?.ahj_name) {
          output['AHJ'] = formData.task.customer_info.ahj_name.trim().toUpperCase();
        }
        break;
        
      case 'UM#':
        if (formData.task?.customer_info?.utility_meter_number) {
          output['UM#'] = formData.task.customer_info.utility_meter_number.trim().toUpperCase();
        }
        break;
        
      case 'MODULES':
      case 'INVERTERS':
      case 'MICROINVERTERS':
      case 'OPTIMIZERS':
      case 'RSD':
        // Equipment items are generated per-system after the main switch
        break;

      default:
        break;
    }
  });

  // Generate equipment output for each system that has data
  activeSystems.forEach(({ data, index }) => {
    const suffix = useSystemSuffix ? ` (S${index})` : '';
    generateSystemEquipmentOutput(data, suffix, output);
  });

  // Add production section if configured (shared across systems)
  const hasProduction = formData.equipment?.production?.production_value && formData.equipment.production.production_value.trim().length > 0;
  if (hasProduction) {
    output['PRODUCTION'] = formData.equipment.production.production_value!.trim();
  }

  // Add MOUNTING SYSTEM if configured
  if (formData.structural?.general_configuration?.mount_system) {
    output['MOUNTING SYSTEM'] = formData.structural.general_configuration.mount_system.toUpperCase();
  }
  
  // Add other mounting configuration if configured
  const hasMountingConfig = formData.structural?.general_configuration?.roof_mount_conduit_size ||
    formData.structural?.general_configuration?.roof_mount_conduit_type ||
    formData.structural?.general_configuration?.bos_conduit ||
    formData.structural?.general_configuration?.trench?.conduit_size ||
    formData.structural?.general_configuration?.trench?.conduit_type ||
    formData.structural?.structures;

  if (hasMountingConfig) {
    let mountingOutput = '';

    // Add roof-mount conduit if configured (combine size + type)
    if (formData.structural.general_configuration?.roof_mount_conduit_size || formData.structural.general_configuration?.roof_mount_conduit_type) {
      const roofConduitParts = [
        formData.structural.general_configuration.roof_mount_conduit_size,
        formData.structural.general_configuration.roof_mount_conduit_type
      ].filter(Boolean);
      mountingOutput += `ROOF-MOUNT CONDUIT: ${roofConduitParts.join(' ').toUpperCase()}\n`;
    }

    // Add BOS conduit if configured
    if (formData.structural.general_configuration?.bos_conduit) {
      mountingOutput += `BOS CONDUIT: ${formData.structural.general_configuration.bos_conduit.toUpperCase()}\n`;
    }

    // Add trench conduit if configured (combine size + type)
    if (formData.structural.general_configuration?.trench?.conduit_size || formData.structural.general_configuration?.trench?.conduit_type) {
      const trenchConduitParts = [
        formData.structural.general_configuration.trench?.conduit_size,
        formData.structural.general_configuration.trench?.conduit_type
      ].filter(Boolean);
      let trenchOutput = `TRENCH CONDUIT: ${trenchConduitParts.join(' ').toUpperCase()}`;
      if (formData.structural.general_configuration.trench?.length || formData.structural.general_configuration.trench?.depth) {
        trenchOutput += ' (';
        if (formData.structural.general_configuration.trench?.length) trenchOutput += `LENGTH: ${formData.structural.general_configuration.trench.length}'`;
        if (formData.structural.general_configuration.trench?.length && formData.structural.general_configuration.trench?.depth) trenchOutput += ', ';
        if (formData.structural.general_configuration.trench?.depth) trenchOutput += `DEPTH: ${formData.structural.general_configuration.trench.depth}'`;
        trenchOutput += ')';
      }
      mountingOutput += trenchOutput + '\n';
    }
    
    // Add structures if configured - process each structure separately
    if (formData.structural.structures) {
      const structureKeys = ['structure_1', 'structure_2', 'structure_3'] as const;
      
      structureKeys.forEach((structKey, index) => {
        const structure = formData.structural.structures![structKey];
        const structureNum = index + 1;
        
        if (structure) {
          // Add STRUCTURE (N) NAME
          if (structure.name) {
            output[`STRUCTURE (${structureNum}) NAME`] = structure.name.toUpperCase();
          }
          
          // Add FRAMING (N)
          if (structure.framing?.size && structure.framing?.type && structure.framing?.rafter_spacing) {
            const framingSize = structure.framing.size;
            const framingType = structure.framing.type;
            const rafterSpacing = structure.framing.rafter_spacing;
            const assumedPart = structure.framing.is_assumed ? ' (ASSUMED - NO MEASUREMENTS)' : '';
            output[`FRAMING (${structureNum})`] = `${framingSize} ${framingType.toUpperCase()} ${rafterSpacing} O.C.${assumedPart}`;
          }
          
          // Add ROOF (N) - combining roof_type and roof_condition
          if (structure.roof_configuration?.roof_type) {
            let roofOutput = structure.roof_configuration.roof_type.toUpperCase();
            if (structure.roof_configuration?.roof_condition) {
              roofOutput += ` (${structure.roof_configuration.roof_condition.toUpperCase()})`;
            }
            output[`ROOF (${structureNum})`] = roofOutput;
          }
          
          // Add REROOF (N)
          if (structure.roof_configuration?.reroof_status) {
            output[`REROOF (${structureNum})`] = structure.roof_configuration.reroof_status.toUpperCase();
          }
          
          // Add BUILDING HEIGHT (N)
          if (structure.roof_configuration?.building_height) {
            output[`BUILDING HEIGHT (${structureNum})`] = structure.roof_configuration.building_height.toUpperCase();
          }
          
          // Add ATTACHMENT (N) ONE
          if (structure.attachments_rails?.attachment_1) {
            output[`ATTACHMENT (${structureNum}) ONE`] = structure.attachments_rails.attachment_1.toUpperCase();
          }
          
          // Add RAIL (N) ONE
          if (structure.attachments_rails?.rail_1?.name) {
            const railName = structure.attachments_rails.rail_1.name;
            const domesticStatus = structure.attachments_rails.rail_1.domestic_status || DomesticStatus.NON_DOM;
            const formattedDomesticStatus = domesticStatus === DomesticStatus.DOM ? 'DOMESTIC' : 'NON-DOMESTIC';
            output[`RAIL (${structureNum}) ONE`] = `${railName.toUpperCase()} (${formattedDomesticStatus})`;
          }
          
          // Add ATTACHMENT (N) TWO
          if (structure.attachments_rails?.attachment_2) {
            output[`ATTACHMENT (${structureNum}) TWO`] = structure.attachments_rails.attachment_2.toUpperCase();
          }
          
          // Add RAIL (N) TWO
          if (structure.attachments_rails?.rail_2?.name) {
            const railName = structure.attachments_rails.rail_2.name;
            const domesticStatus = structure.attachments_rails.rail_2.domestic_status || DomesticStatus.NON_DOM;
            const formattedDomesticStatus = domesticStatus === DomesticStatus.DOM ? 'DOMESTIC' : 'NON-DOMESTIC';
            output[`RAIL (${structureNum}) TWO`] = `${railName.toUpperCase()} (${formattedDomesticStatus})`;
          }
        }
      });
    }
    
    // Remove trailing newline
    if (mountingOutput.endsWith('\n')) {
      mountingOutput = mountingOutput.slice(0, -1);
    }
    
    // Only add mounting content to output if there's actual content
    if (mountingOutput.trim().length > 0) {
      // Parse the mounting output to extract individual fields
      const lines = mountingOutput.split('\n');
      
      lines.forEach(line => {
        const trimmedLine = line.trim();
        
        if (trimmedLine.includes('ROOF-MOUNT CONDUIT:')) {
          const [, value] = trimmedLine.split(':');
          if (value) output['ROOF-MOUNT CONDUIT'] = value.trim();
        } else if (trimmedLine.includes('BOS CONDUIT:')) {
          const [, value] = trimmedLine.split(':');
          if (value) output['BOS CONDUIT'] = value.trim();
        } else if (trimmedLine.includes('TRENCH CONDUIT:')) {
          const [, ...valueParts] = trimmedLine.split(':');
          if (valueParts.length) output['TRENCH CONDUIT'] = valueParts.join(':').trim();
        }
      });
    }
  }

  // Add MSP section if configured
  const hasMsp = formData.electrical?.msp?.buss_rating && 
    formData.electrical.msp.main_breaker_rating && 
    formData.electrical.msp.manufacturer && 
    formData.electrical.msp.location;
  if (hasMsp) {
    const mspBuss = formData.electrical.msp.buss_rating!;
    const mspBreaker = formData.electrical.msp.main_breaker_rating!;
    const mspManufacturer = formData.electrical.msp.manufacturer!;
    const mspLocation = formData.electrical.msp.location!;
    
    // Build the MSP output with new/existing prefixes
    let mspOutput = '';
    
    // Add MSP new/existing prefix
    if (formData.electrical.msp.msp_new_existing) {
      mspOutput += `(${formData.electrical.msp.msp_new_existing})`;
    }
    
    mspOutput += `${mspBuss}A/`;
    
    // Add main breaker new/existing prefix
    if (formData.electrical.msp.main_breaker_new_existing) {
      mspOutput += `(${formData.electrical.msp.main_breaker_new_existing})`;
    }
    
    mspOutput += `${mspBreaker}A ${mspManufacturer} (${mspLocation})`;
    
    // Add style and type if present
    if (formData.electrical.msp.style || formData.electrical.msp.type) {
      const extras: string[] = [];
      if (formData.electrical.msp.style) extras.push(formData.electrical.msp.style);
      if (formData.electrical.msp.type) extras.push(formData.electrical.msp.type);
      mspOutput += ` - ${extras.join(' - ')}`;
    }
    
    output['MSP'] = mspOutput;
  }

  // Add MBE section if configured
  const hasMbe = formData.electrical?.mbe?.buss_rating && formData.electrical.mbe.main_breaker_rating;
  if (hasMbe) {
    const mbeBuss = formData.electrical.mbe.buss_rating!;
    const mbeMainBreakerRating = formData.electrical.mbe.main_breaker_rating!;
    
    // Build the MBE output: (N) {BUS} (E) {MAIN_BREAKER_RATING}
    let mbeOutput = '';
    
    // Add MBE new/existing prefix
    if (formData.electrical.mbe.mbe_new_existing) {
      mbeOutput += `(${formData.electrical.mbe.mbe_new_existing})`;
    }
    
    mbeOutput += `${mbeBuss}A `;
    
    // Add main breaker new/existing prefix
    if (formData.electrical.mbe.main_breaker_new_existing) {
      mbeOutput += `(${formData.electrical.mbe.main_breaker_new_existing})`;
    }
    
    mbeOutput += `${mbeMainBreakerRating}A`;
    
    output['MBE'] = mbeOutput;
  }

  // Add BLP section if configured
  const hasBlp = formData.electrical?.blp?.buss_rating && formData.electrical.blp.main_breaker_rating;
  if (hasBlp) {
    const blpBuss = formData.electrical.blp.buss_rating!;
    const blpMainBreakerRating = formData.electrical.blp.main_breaker_rating!;
    
    // Build the BLP output: (N) {BUS} (E) {MAIN_BREAKER_RATING}
    let blpOutput = '';
    
    // Add BLP new/existing prefix
    if (formData.electrical.blp.blp_new_existing) {
      blpOutput += `(${formData.electrical.blp.blp_new_existing})`;
    }
    
    blpOutput += `${blpBuss}A `;
    
    // Add main breaker new/existing prefix
    if (formData.electrical.blp.main_breaker_new_existing) {
      blpOutput += `(${formData.electrical.blp.main_breaker_new_existing})`;
    }
    
    blpOutput += `${blpMainBreakerRating}A`;
    
    output['BLP'] = blpOutput;
  }

  // Add ATS section if configured - build incrementally
  const hasAtsNewExisting = formData.electrical?.ats?.ats_new_existing;
  const hasAtsBuss = formData.electrical?.ats?.buss_rating;
  
  if (hasAtsNewExisting && hasAtsBuss) {
    let atsOutput = '';
    
    // Start with new/existing and buss rating
    atsOutput += `(${formData.electrical.ats.ats_new_existing})${formData.electrical.ats.buss_rating}A`;
    
    // Add panel name if selected
    if (formData.electrical.ats.panel_name) {
      atsOutput += ` ${formData.electrical.ats.panel_name}`;
    }
    
    // Add main breaker if selected
    if (formData.electrical.ats.main_breaker_rating) {
      atsOutput += ' WITH ';
      
      // Add main breaker new/existing prefix if selected
      if (formData.electrical.ats.main_breaker_new_existing) {
        atsOutput += `(${formData.electrical.ats.main_breaker_new_existing})`;
      }
      
      atsOutput += `${formData.electrical.ats.main_breaker_rating}A MAIN BREAKER`;
    }
    
    // Add generator name if selected
    if (formData.electrical.ats.generator_name) {
      atsOutput += ` ${formData.electrical.ats.generator_name}`;
    }
    
    output['ATS'] = atsOutput;
  }

  // Add PRODUCTION METER section if configured
  const hasProductionMeter = formData.electrical?.production_meter?.meter_name_model;
  if (hasProductionMeter) {
    const productionMeterNameModel = formData.electrical.production_meter.meter_name_model!;
    
    // Build the Production Meter output: (N) {meter_name_model}
    let productionMeterOutput = '';
    
    // Add Production Meter new/existing prefix
    if (formData.electrical.production_meter.new_existing) {
      productionMeterOutput += `(${formData.electrical.production_meter.new_existing}) `;
    }
    
    productionMeterOutput += productionMeterNameModel;
    
    output['PRODUCTION METER'] = productionMeterOutput;
  }

  // Add SUBPANEL section if configured
  const hasSubpanel = formData.electrical?.subpanel?.subpanel_size && formData.electrical.subpanel.main_breaker_rating;
  if (hasSubpanel) {
    // Build the Subpanel output: (E) 200A WITH (E) 100A MAIN BREAKER
    let subpanelOutput = '';
    
    // Add subpanel new/existing prefix and size
    if (formData.electrical.subpanel.subpanel_new_existing) {
      subpanelOutput += `(${formData.electrical.subpanel.subpanel_new_existing})`;
    }
    if (formData.electrical.subpanel.subpanel_size) {
      subpanelOutput += formData.electrical.subpanel.subpanel_size.toUpperCase();
    }
    
    // Add main breaker
    if (formData.electrical.subpanel.main_breaker_rating) {
      subpanelOutput += ' WITH ';
      if (formData.electrical.subpanel.main_breaker_new_existing) {
        subpanelOutput += `(${formData.electrical.subpanel.main_breaker_new_existing})`;
      }
      subpanelOutput += `${formData.electrical.subpanel.main_breaker_rating}A MAIN BREAKER`;
    }
    
    // Add feeding method info
    if (formData.electrical.subpanel.feeding_method) {
      if (formData.electrical.subpanel.feeding_method === 'Breaker in MSP' && formData.electrical.subpanel.ocpd_size) {
        // If feeding method is "Breaker in MSP" and OCPD size is set
        subpanelOutput += ` (FED BY ${formData.electrical.subpanel.ocpd_size.toUpperCase()} IN MSP)`;
      } else {
        // Otherwise just show feeding method
        subpanelOutput += ` (FED BY ${formData.electrical.subpanel.feeding_method.toUpperCase()})`;
      }
    }
    
    // Add PV breaker info if configured
    if (formData.electrical.subpanel.pv_breaker_size || formData.electrical.subpanel.pv_breaker_location) {
      subpanelOutput += ' (PV BREAKER ';
      
      if (formData.electrical.subpanel.pv_breaker_size) {
        subpanelOutput += formData.electrical.subpanel.pv_breaker_size.toUpperCase();
      }
      
      if (formData.electrical.subpanel.pv_breaker_location) {
        if (formData.electrical.subpanel.pv_breaker_size) {
          subpanelOutput += ' - ';
        }
        subpanelOutput += formData.electrical.subpanel.pv_breaker_location.toUpperCase();
      }
      
      subpanelOutput += ')';
    }
    
    output['SUBPANEL'] = subpanelOutput;
  }

  // Add POI section if configured
  const hasPoi = formData.electrical?.poi?.selected_poi && formData.electrical.poi.selected_poi.trim();
  if (hasPoi) {
    let poiOutput = formData.electrical.poi.selected_poi!.toUpperCase();
    
    // Check if it's a BREAKER TIE-IN method
    const isBreakerTieIn = ['BREAKER TIE-IN MSP', 'BREAKER TIE-IN SUB'].includes(formData.electrical.poi.selected_poi!);
    
    if (isBreakerTieIn) {
      // For BREAKER TIE-IN methods, add breaker size if available
      if (formData.electrical.poi.breaker_size) {
        poiOutput += ` (${formData.electrical.poi.breaker_size}A)`;
      }
    } else {
      // For other methods (that have locations), add location if available
      if (formData.electrical.poi.location) {
        if (formData.electrical.poi.location === 'ATS') {
          // Special handling for ATS location
          poiOutput += ' (ATS';
          if (formData.electrical.poi.breaker_size) {
            poiOutput += `-${formData.electrical.poi.breaker_size}`;
          }
          poiOutput += ')';
        } else {
          // Regular location
          poiOutput += ` (${formData.electrical.poi.location})`;
        }
      }
    }
    
    output['POI'] = poiOutput;
  }

  // Add DISCO sections if configured (multiple discos)
  const discoConfigurations = [
    formData.electrical?.disco_1,
    formData.electrical?.disco_2,
    formData.electrical?.disco_3,
    formData.electrical?.disco_4,
    formData.electrical?.disco_5
  ].map((disco, index) => {
    // Only include discos that have at least rating, type, and model
    if (disco && disco.rating && disco.type && disco.model) {
      return {
        ...disco,
        discoNumber: index + 1
      };
    }
    return null;
  }).filter(disco => disco !== null);

  if (discoConfigurations.length > 0) {
    discoConfigurations.forEach((disco) => {
      const discoNumber = disco!.discoNumber;
      const rating = disco!.rating || 0;
      const type = disco!.type?.toUpperCase() || '';
      const location = disco!.location || '';
      const text = disco!.text || '';
      const fuses = disco!.fuses;
      
      // Format: 100A PV FUSED DISCO WITH 100A FUSES (NORTH OUTSIDE WALL - AC DISCONNECT)
      let discoOutput = `${rating}A PV ${type} DISCO`;
      
      if (type === 'FUSED' && fuses) {
        discoOutput += ` WITH ${fuses}A FUSES`;
      }
      
      // Add location and text in parentheses if available
      const locationTextParts = [];
      if (location) locationTextParts.push(location.toUpperCase());
      if (text) locationTextParts.push(text.toUpperCase());
      
      if (locationTextParts.length > 0) {
        discoOutput += ` (${locationTextParts.join(' - ')})`;
      }
      
      output[`DISCO ${discoNumber}`] = discoOutput;
    });
  }

  // Add UTILITY CONFIG section if configured (from electrical utility tab)
  const hasUtilityConfig = formData.electrical?.utility?.service_feed_source && formData.electrical.utility.distance_from_um_callout;
  if (hasUtilityConfig) {
    let utilityOutput = `${formData.electrical.utility.service_feed_source} (${formData.electrical.utility.distance_from_um_callout})`;
    
    if (formData.electrical.utility.utility_meter_base_new_existing) {
      utilityOutput += ` (${formData.electrical.utility.utility_meter_base_new_existing}) UM`;
      
      if (formData.electrical.utility.utility_meter_base_new_existing === NewExistingType.NEW && formData.electrical.utility.utility_meter_base_name_model) {
        utilityOutput += ` ${formData.electrical.utility.utility_meter_base_name_model}`;
      }
    }
    
    output['UTILITY CONFIG'] = utilityOutput;
  }

  // Add BOM data
  if (formData.bom?.lgcy?.lis_number?.trim()) {
    output['LIS#'] = formData.bom.lgcy.lis_number.trim().toUpperCase();
  }

  // Add BOS LOCATION section if configured
  const hasBosLocation = formData.documentation?.bos_location_value && formData.documentation.bos_location_value.trim().length > 0;
  if (hasBosLocation) {
    output['BOS LOCATION'] = formData.documentation.bos_location_value!.trim().toUpperCase();
  }

  // Add AHJ section from customer info
  if (formData.task?.customer_info?.ahj_name && formData.task.customer_info.ahj_name.trim().length > 0) {
    output['AHJ'] = formData.task.customer_info.ahj_name.trim().toUpperCase();
  }

  // Add UM# from customer info
  if (formData.task?.customer_info?.utility_meter_number && formData.task.customer_info.utility_meter_number.trim().length > 0) {
    output['UM#'] = formData.task.customer_info.utility_meter_number.trim().toUpperCase();
  }

  if (formData.documentation?.install_notes_value?.trim()) {
    output['INSTALL NOTES'] = formData.documentation.install_notes_value.trim().toUpperCase();
  }

  if (formData.documentation?.miscellaneous_notes_value?.trim()) {
    output['MISCELLANEOUS NOTES'] = formData.documentation.miscellaneous_notes_value.trim().toUpperCase();
  }

  if (formData.documentation?.aurora_value?.trim()) {
    output['AURORA'] = formData.documentation.aurora_value.trim();
  }

  if (formData.documentation?.gdrive_link_value?.trim()) {
    output['GDRIVE LINK'] = formData.documentation.gdrive_link_value.trim();
  }

  if (formData.documentation?.notion_ahj_value?.trim()) {
    output['NOTION (AHJ)'] = formData.documentation.notion_ahj_value.trim();
  }

  if (formData.documentation?.notion_ux_value?.trim()) {
    output['NOTION (UX)'] = formData.documentation.notion_ux_value.trim();
  }

  if (formData.documentation?.notion_cx_value?.trim()) {
    output['NOTION (CX)'] = formData.documentation.notion_cx_value.trim();
  }

  if (formData.documentation?.nec_value?.trim()) {
    output['NEC'] = formData.documentation.nec_value.trim().toUpperCase();
  }

  if (formData.documentation?.ibc_value?.trim()) {
    output['IBC'] = formData.documentation.ibc_value.trim().toUpperCase();
  }

  if (formData.documentation?.irc_value?.trim()) {
    output['IRC'] = formData.documentation.irc_value.trim().toUpperCase();
  }

  if (formData.documentation?.ifc_value?.trim()) {
    output['IFC'] = formData.documentation.ifc_value.trim().toUpperCase();
  }

  if (formData.documentation?.poi_notes_value?.trim()) {
    output['POI NOTES'] = formData.documentation.poi_notes_value.trim().toUpperCase();
  }

  if (formData.documentation?.design_notes_value?.trim()) {
    output['DESIGN NOTES'] = formData.documentation.design_notes_value.trim().toUpperCase();
  }

  if (formData.documentation?.lgcy_lis_number_value?.trim()) {
    output['LGCY LIS#'] = formData.documentation.lgcy_lis_number_value.trim().toUpperCase();
  }

  return output;
};